"use client";

import React from "react";

import { useCompany } from "@/lib/hooks/api/companies";
import { useTicket } from "@/lib/hooks/api/tickets";
import { useEntityContextOptional } from "@/components/shared/UniversalDetailPage/contexts/EntityContextProvider";
import { useModalControls } from "@/lib/modals";

import {
  type CompanyDetailsTabProps,
  type CompanyRow,
  asStringId,
  companyWithId,
} from "./company-details/types";
import {
  DenormalizedState,
  EmptyState,
  ErrorState,
  LoadingState,
} from "./company-details/CompanyDetailsStates";
import { CompanyDetailsCard } from "./company-details/CompanyDetailsCard";

/**
 * Reusable Company Details Tab Component
 *
 * Data resolution priority:
 * 1. Preloaded company data
 * 2. Nested company data (from related entity)
 * 3. Fetch by company_id
 */
export function CompanyDetailsTab({
  companyId: propCompanyId,
  companyData,
  companyName: propCompanyName,
  embedded = false,
  onViewFullCompany,
  nestedCompanyData,
  nestedCompanyId,
  ticketId,
}: CompanyDetailsTabProps) {
  const { openModal: openCompanyModal } = useModalControls("companies-detail");

  const entityContext = useEntityContextOptional();
  const contextCompanyId = entityContext?.entityData?.company_id;
  const contextCompanyName = entityContext?.entityData?.company_name;

  const entryCompanyId = asStringId(propCompanyId) ?? asStringId(contextCompanyId);
  const nestedId = asStringId(nestedCompanyId);

  const targetCompanyIdBeforeTicket = entryCompanyId ?? nestedId ?? null;
  const needsTicketFetch = !targetCompanyIdBeforeTicket && !!ticketId;

  const ticketQuery = useTicket(ticketId ?? "", { enabled: needsTicketFetch });

  const ticketData = ticketQuery.data as
    | { company_id?: string | null; companies?: unknown }
    | undefined;

  const companyIdFromTicket =
    typeof ticketData?.company_id === "string" && ticketData.company_id
      ? ticketData.company_id
      : null;

  const resolvedCompanyId = targetCompanyIdBeforeTicket ?? companyIdFromTicket ?? null;

  const candidates = [companyData, nestedCompanyData, ticketData?.companies];

  const preloaded = candidates.find((c) => {
    const cw = companyWithId(c);
    return !!(cw?.id && resolvedCompanyId && String(cw.id) === String(resolvedCompanyId));
  });
  const preloadedCompany = companyWithId(preloaded);
  const hasPreloadedCompany = !!preloadedCompany;

  const { data: fetchedCompany, isLoading, error } = useCompany(resolvedCompanyId, {
    enabled: !!resolvedCompanyId && !hasPreloadedCompany,
  });

  const company = preloadedCompany || (fetchedCompany as CompanyRow | undefined) || null;
  const resolvedId = company?.id ?? resolvedCompanyId;

  const loading =
    (needsTicketFetch && ticketQuery.isLoading && !preloadedCompany && !fetchedCompany) ||
    (!!resolvedCompanyId && !hasPreloadedCompany && isLoading && !company);

  const queryError =
    needsTicketFetch && ticketQuery.isError
      ? ticketQuery.error
      : resolvedCompanyId && !hasPreloadedCompany && error
        ? error
        : null;

  const hasDenormalized = !!(
    (propCompanyName || contextCompanyName) && !resolvedCompanyId
  );

  const handleViewFullCompany = () => {
    if (resolvedId) {
      if (onViewFullCompany) {
        onViewFullCompany(resolvedId);
      } else {
        openCompanyModal({ companiesId: resolvedId });
      }
    }
  };

  const headerTitle = embedded ? "Company" : "Company details";

  if (loading && !company) {
    return <LoadingState embedded={embedded} />;
  }

  if (queryError && !company) {
    return (
      <ErrorState
        embedded={embedded}
        resolvedCompanyId={resolvedCompanyId}
        ticketId={ticketId}
      />
    );
  }

  if (!company) {
    if (hasDenormalized) {
      return (
        <DenormalizedState
          embedded={embedded}
          headerTitle={headerTitle}
          companyName={(propCompanyName || contextCompanyName) as string}
          ticketId={ticketId}
        />
      );
    }
    return <EmptyState embedded={embedded} />;
  }

  return (
    <CompanyDetailsCard
      company={company}
      resolvedId={resolvedId}
      embedded={embedded}
      headerTitle={headerTitle}
      onViewFullCompany={handleViewFullCompany}
    />
  );
}
